import { Flex, VStack } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserState } from '~/atoms/UserState'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import {
  GroupedScrollingList,
  GroupedScrollToIndexParams,
  ScrollToIndexType,
} from '~/components/shared/GroupedScrollingList'
import { GroupedScrollingListHeader } from '~/components/shared/GroupedScrollingListGroupHeader'
import { ScrollingList } from '~/components/shared/ScrollingList'
import { SearchInput } from '~/components/shared/SearchInput'
import { ContactsListEmptyNotice } from '~/pages/Contacts/ContactsListEmptyNotice'
import { ContactsListItem } from '~/pages/Contacts/ContactsListItem'
import { SearchValue } from '~/types/Shared'
import { hasValue } from '~/utils/common'

import { Contact, ContactSource, ContactSourceType } from '@mangovoice/types'

import { useContactsListViewStyle } from './componentStyles/ContactsListView.style'

interface ContactsListViewProps {
  selectedContactSource?: ContactSource
  contactSources?: ContactSource[]
  contacts?: Contact[]
  contactsLoading?: boolean
  selectedContactId?: number | string
  searchValue?: SearchValue
  autoScrollList?: boolean
  onContactSourceSelected?: (contactSource?: ContactSource) => void
  onContactSelected?: (contact: Contact) => void
  onScrollEndReached?: () => void
  onSearchTermChanged?: (searchValue: SearchValue) => void
}
export const ContactsListView = ({
  selectedContactSource,
  contactSources,
  contacts,
  contactsLoading,
  selectedContactId,
  searchValue,
  autoScrollList,
  onContactSourceSelected,
  onContactSelected,
  onScrollEndReached,
  onSearchTermChanged,
}: ContactsListViewProps) => {
  const { t } = useTranslation()
  const { pbx } = useUserState()
  const isGrove = pbx?.features?.is_grove ?? false

  const getContactGroup = (contact: Contact) => {
    let itemName = contact.given_name.trim()
    if ((itemName?.length ?? 0) === 0) {
      itemName = contact.family_name.trim()
    }
    const firstNameFirstLetter = itemName[0]
    return firstNameFirstLetter.toUpperCase()
  }

  const contactsGroupMap: Map<string, Contact[]> = useMemo(() => {
    if (!contacts) {
      return new Map<string, Contact[]>()
    }

    const sorted = contacts.sort((a, b) => {
      let aName = a.given_name
      let bName = b.given_name

      if ((aName?.length ?? 0) === 0) {
        aName = a.family_name
      }

      if ((bName?.length ?? 0) === 0) {
        bName = b.family_name
      }

      return aName.localeCompare(bName)
    })

    const map = new Map<string, Contact[]>()
    for (const contact of sorted) {
      const contactGroup = getContactGroup(contact)

      if (!map.has(contactGroup)) {
        map.set(contactGroup, [])
      }

      map.get(contactGroup)?.push(contact)
    }

    return map
  }, [contacts])

  const contactSourceNodes = useMemo(() => {
    const nodes = contactSources?.map((source) => {
      return {
        value: source.id,
        content: source.name,
      }
    })
    return [
      { value: 0, content: t('contactsListView.contactSourceContent') },
      ...(nodes ?? []),
    ]
  }, [contactSources])

  const contactsIndicesMap = useMemo(() => {
    if (!contactsGroupMap) {
      return new Map<string, number>()
    }

    const map = new Map<string | number, number>()
    let rollingCount = 0
    for (const key of contactsGroupMap.keys()) {
      const contacts = contactsGroupMap.get(key)
      map.set(key, rollingCount)

      if (!contacts) {
        continue
      }

      for (const contact of contacts) {
        map.set(contact.id, rollingCount++)
      }
    }
    return map
  }, [contactsGroupMap])

  const handleBuildItemContent = (
    index: number,
    contact: Contact,
    isSelected: boolean,
  ) => {
    const fullName = `${contact.given_name}${' ' + contact.family_name}`.trim()
    const contactPrimaryNumber =
      contact.primary_phone_number ?? contact.phone_numbers?.[0]

    const extensionSourceId = contactSources?.find(
      (cs) => cs.source === ContactSourceType.Extensions,
    )?.id
    let extension: string
    if (
      contactPrimaryNumber &&
      contact.contact_source_id === extensionSourceId
    ) {
      extension = contactPrimaryNumber.number!
    }

    return (
      <ContactsListItem
        key={contact.id}
        name={fullName}
        index={contact.id}
        searchTerm={searchValue?.value}
        isSelected={isSelected}
        extension={extension!}
        onClick={(_) => onContactSelected?.(contact)}
      />
    )
  }

  const handleBuildGroupContent = (index: number, data: string) => {
    return (
      <GroupedScrollingListHeader
        mb="0.5rem"
        index={index}
        role="heading"
        aria-level={2}
      >
        {data}
      </GroupedScrollingListHeader>
    )
  }

  const handleContactSourceSelectedInternal = (id: number, index: number) => {
    if (index === 0) {
      onContactSourceSelected?.()
    }
    const contactSource = contactSources?.[index - 1]
    onContactSourceSelected?.(contactSource)
  }

  const selectedContactSourceIndex = useMemo(() => {
    if (!selectedContactSource) return 0

    const index =
      contactSources?.findIndex(
        (source) => source.id === selectedContactSource.id,
      ) ?? -1
    return index + 1
  }, [selectedContactSource])

  const styles = useContactsListViewStyle()

  // Extract layout properties from styles to apply as native props
  const vstackLayoutProps = {
    display: styles.vstack?.display,
    height: styles.vstack?.height,
    alignSelf: styles.vstack?.alignSelf,
    gap: styles.vstack?.gap,
  }

  const searchContainerLayoutProps = {
    display: styles.searchContainer?.display,
    flexDirection: styles.searchContainer?.flexDirection,
    justifyContent: styles.searchContainer?.justifyContent,
    alignItems: styles.searchContainer?.alignItems,
    alignSelf: styles.searchContainer?.alignSelf,
  }

  const dropdownContainerLayoutProps = {
    width: styles.dropdownContainer?.width,
    padding: styles.dropdownContainer?.padding,
    justifyContent: styles.dropdownContainer?.justifyContent,
    alignItems: styles.dropdownContainer?.alignItems,
    gap: styles.dropdownContainer?.gap,
    alignSelf: styles.dropdownContainer?.alignSelf,
    zIndex: styles.dropdownContainer?.zIndex,
  }

  const scrollingListLayoutProps = {
    display: styles.scrollingList?.display,
    flex: styles.scrollingList?.flex,
    width: styles.scrollingList?.width,
    height: styles.scrollingList?.height,
    paddingX: styles.scrollingList?.paddingX,
    paddingY: styles.scrollingList?.paddingY,
  }

  const selectedItemIndex = contactsIndicesMap.get(selectedContactId ?? -1)
  const scrollToIndex: GroupedScrollToIndexParams | undefined =
    autoScrollList && selectedItemIndex !== undefined
      ? {
          index: selectedItemIndex,
          type: ScrollToIndexType.Item,
          options: { align: 'start', behavior: 'auto' },
        }
      : undefined

  const listAreaContent = useMemo(() => {
    const shouldShowEmptyContactsNotice =
      !contactsLoading && (!contacts || contacts.length === 0)
    //contacts are done loading, there are no contacts, and there is no search term
    if (shouldShowEmptyContactsNotice) {
      //show empty state
      return <ContactsListEmptyNotice searchTerm={searchValue?.value} />
    }

    const shouldShowSearchResults =
      contacts && contacts.length > 0 && hasValue(searchValue?.value)

    if (shouldShowSearchResults) {
      //show search results
      return (
        <ScrollingList
          css={styles.scrollingList}
          {...scrollingListLayoutProps}
          showLoaderFooter={contactsLoading}
          scrollToIndex={scrollToIndex}
          selectedItemIndex={selectedItemIndex}
          scrollbarOptions={{ showOnHoverOnly: true }}
          itemData={contacts ?? []}
          onBuildItemContent={handleBuildItemContent}
          onEndReached={onScrollEndReached}
        />
      )
    }

    //default, show groupedContacts
    return (
      <GroupedScrollingList
        css={styles.scrollingList}
        {...scrollingListLayoutProps}
        scrollToIndex={scrollToIndex}
        groupItemData={contactsGroupMap}
        selectedItemIndex={selectedItemIndex}
        loadThresholdPercentage={0.2}
        scrollbarOptions={{ showOnHoverOnly: true }}
        onBuildGroupContent={handleBuildGroupContent}
        onBuildItemContent={handleBuildItemContent}
        onEndReached={onScrollEndReached}
        showLoaderFooter={contactsLoading}
      />
    )
  }, [contacts, contactsLoading, searchValue, selectedItemIndex])

  return (
    <VStack css={styles.vstack} {...vstackLayoutProps}>
      <Flex css={styles.searchContainer} {...searchContainerLayoutProps}>
        <SearchInput
          p="1rem"
          placeholder={t('contactsListView.searchPlaceholder')}
          inputLabel={t('contactsListView.searchLabel')}
          onChange={onSearchTermChanged}
        />
      </Flex>
      {!isGrove && (
        <Flex
          css={styles.dropdownContainer}
          {...dropdownContainerLayoutProps}
          borderBottom="2px solid"
          borderColor="border.default"
        >
          <DropdownSelect
            items={contactSourceNodes}
            selectedIndex={selectedContactSourceIndex}
            onSelect={handleContactSourceSelectedInternal}
          />
        </Flex>
      )}
      {listAreaContent}
    </VStack>
  )
}
